import { Clock, Calendar } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { SprintDetail } from '@/services/sprint.service';

interface SprintDateHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sprint: SprintDetail;
}

export function SprintDateHistoryDialog({ open, onOpenChange, sprint }: SprintDateHistoryDialogProps) {
  // Display sprint dates and key timestamps
  const allEvents: Array<{ label: string; date: string | null; icon: 'calendar' | 'clock'; color: string }> = [
    { label: 'Created', date: sprint.created_at, icon: 'clock' as const, color: 'text-muted-foreground' },
    { label: 'Start Date', date: sprint.start_date, icon: 'calendar' as const, color: 'text-blue-600' },
    { label: 'End Date', date: sprint.end_date, icon: 'calendar' as const, color: 'text-blue-600' },
    { label: 'Activated', date: sprint.activated_at ?? null, icon: 'clock' as const, color: 'text-emerald-600' },
    { label: 'Completed', date: sprint.completed_at ?? null, icon: 'clock' as const, color: 'text-violet-600' },
    { label: 'Cancelled', date: sprint.cancelled_at ?? null, icon: 'clock' as const, color: 'text-red-600' },
  ];
  const events = allEvents.filter((e) => e.date);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Sprint Date History</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {events.map((event) => (
            <div key={event.label} className="flex items-center gap-3 py-2 border-b last:border-0">
              <div className={event.color}>
                {event.icon === 'calendar' ? <Calendar className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{event.label}</p>
                <p className="text-xs text-muted-foreground">
                  {event.date
                    ? new Date(event.date).toLocaleString('en-US', {
                        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                        ...(event.icon === 'clock' ? { hour: '2-digit', minute: '2-digit' } : {}),
                      })
                    : '—'}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-xs text-muted-foreground mt-2">
          Sprint duration:{' '}
          {sprint.start_date && sprint.end_date
            ? `${Math.ceil((new Date(sprint.end_date).getTime() - new Date(sprint.start_date).getTime()) / (1000 * 60 * 60 * 24))} days`
            : '—'}
        </div>
      </DialogContent>
    </Dialog>
  );
}
